/**
 * Axios API 服务层
 * 统一封装所有与后端 NovelAxiosController2 的 HTTP 请求
 * 替换原有的 WebSocket 通信方式
 */
import axios from 'axios';

// 创建 axios 实例
const apiClient = axios.create({
    // baseURL: 'http://localhost:8081/api/novel',
    baseURL: '/api/novel',
    timeout: 30000,
    headers: {
        'Content-Type': 'application/json'
    }
});

// 请求拦截器：自动添加 Authorization 头
apiClient.interceptors.request.use(config => {
    const token = localStorage.getItem('writer-auth');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// 响应拦截器：统一处理错误
apiClient.interceptors.response.use(
    response => {
        const data = response.data;
        // Result 结构：{ code: 0, msg: "success", data: ... }
        // code=0 成功，code=1 失败
        if (data.code !== 0) {
            return Promise.reject(new Error(data.msg || '请求失败'));
        }
        return data;
    },
    error => {
        console.error('API 请求错误:', error);
        return Promise.reject(error);
    }
);

// ==================== 账号 ====================

/**
 * 获取所有账号
 * GET /api/novel/accounts
 */
export const getAccounts = () => {
    return apiClient.get('/accounts');
};

// ==================== 小说 ====================

/**
 * 获取所有小说列表
 * GET /api/novel/list
 */
export const getNovels = () => {
    return apiClient.get('/list');
};

/**
 * 获取单本小说详情
 * GET /api/novel/{novelId}
 * @param {number} novelId - 小说ID
 */
export const getOneNovel = (novelId) => {
    return apiClient.get(`/${novelId}`);
};

// ==================== 章节 ====================

/**
 * 获取小说章节列表
 * GET /api/novel/{novelId}/chapters
 * @param {number} novelId - 小说ID
 */
export const getChapterIntroduce = (novelId) => {
    return apiClient.get(`/${novelId}/chapters`);
};

/**
 * 获取章节内容
 * GET /api/novel/{novelId}/chapter/{chapterId}
 * @param {number} novelId - 小说ID
 * @param {number} chapterId - 章节ID
 */
export const getChapterContent = (novelId, chapterId) => {
    return apiClient.get(`/${novelId}/chapter/${chapterId}`);
};

/**
 * 更新章节
 * PUT /api/novel/chapter
 * @param {Object} newChapter - 章节介绍对象
 * @param {Object} newContent - 章节内容对象
 */
export const updateChapter = (newChapter, newContent) => {
    return apiClient.put('/chapter', {
        newChapter,
        newContent
    });
};

/**
 * 删除章节
 * DELETE /api/novel/{novelId}/chapter/{chapterId}
 * @param {number} novelId - 小说ID
 * @param {number} chapterId - 章节ID
 */
export const deleteChapter = (novelId, chapterId) => {
    return apiClient.delete(`/${novelId}/chapter/${chapterId}`);
};

// ==================== 草稿 ====================

/**
 * 获取草稿列表
 * GET /api/novel/{novelId}/drafts
 * @param {number} novelId - 小说ID
 */
export const getDraftIntroduce = (novelId) => {
    return apiClient.get(`/${novelId}/drafts`);
};

/**
 * 获取草稿内容
 * GET /api/novel/{novelId}/draft/{chapterId}
 * @param {number} novelId - 小说ID
 * @param {number} chapterId - 草稿章节ID
 */
export const getDraftContent = (novelId, chapterId) => {
    return apiClient.get(`/${novelId}/draft/${chapterId}`);
};

/**
 * 新建草稿
 * POST /api/novel/{novelId}/draft
 * @param {number} novelId - 小说ID
 */
export const createDraft = (novelId) => {
    return apiClient.post(`/${novelId}/draft`);
};

/**
 * 更新草稿
 * PUT /api/novel/draft
 * @param {Object} newChapter - 草稿介绍对象
 * @param {Object} newContent - 草稿内容对象
 */
export const updateDraft = (newChapter, newContent) => {
    return apiClient.put('/draft', {
        newChapter,
        newContent
    });
};

/**
 * 草稿转正（发布）
 * POST /api/novel/draft/publish
 * @param {Object} newChapter - 草稿介绍对象
 * @param {Object} newContent - 草稿内容对象
 */
export const publishDraft = (newChapter, newContent) => {
    return apiClient.post('/draft/publish', {
        newChapter,
        newContent
    });
};

/**
 * 删除草稿
 * DELETE /api/novel/{novelId}/draft/{chapterId}
 * @param {number} novelId - 小说ID
 * @param {number} chapterId - 草稿章节ID
 */
export const deleteDraft = (novelId, chapterId) => {
    return apiClient.delete(`/${novelId}/draft/${chapterId}`);
};

/**
 * 更新小说
 * POST /api/upload/novel-update
 * @param {Object} novelData - 小说数据 { id, title, description, coverImage, type }
 */
export const updateNovel = (novelData) => {
    return apiClient.post('/upload/novel-update', novelData);
};

/**
 * 删除小说
 * POST /api/upload/deleteNovel
 * @param {number} id - 小说ID
 */
export const deleteNovel = (id) => {
    return apiClient.post('/upload/deleteNovel', { id });
};

/**
 * 上传封面图片
 * POST /api/upload/cover
 * @param {FormData} formData - 包含文件字段 'file' 的 FormData
 */
export const uploadCover = (formData) => {
    return apiClient.post('/upload/cover', formData, {
        headers: {
            'Content-Type': 'multipart/form-data'
        }
    });
};

export default apiClient;
